<template>
  <div data-test-id="new-room-from" class="v3-form adult-form">
    <div class="row">
      <div class="d-flex flex-column">
        <Label
          required
          :html-for="fieldIds.name"
          :label-id="fieldIds.nameLabel"
        >
          Location name
        </Label>
        <InputField
          :id="fieldIds.name"
          :label-id="fieldIds.nameLabel"
          :aria-required="true"
          :disabled="isIconMode"
          v-model="state.form.name"
          :invalid-feedback="
            errors.name ||
            state.serverErrors.name == 'This room name already exists'
              ? 'This location name already exists'
              : state.serverErrors.name
          "
        ></InputField>
      </div>
      <div class="mt-3">
        <Label :label-id="fieldIds.iconLabel">Icon</Label>
        <IconCarousel
          :aria-labelledby="fieldIds.iconLabel"
          :per-page="10"
          :mouse-drag="true"
          v-model="state.form.icon"
        ></IconCarousel>
      </div>
      <div class="mt-3">
        <Label
          required
          :html-for="fieldIds.tripType"
          :label-id="fieldIds.tripTypeLabel"
        >
          Trip type
        </Label>
        <CustomSelect
          :input-id="fieldIds.tripType"
          accessible-label="Trip type"
          :required="true"
          hide-floating-label
          :disabled="isIconMode"
          :close-on-select="true"
          :options="tripTypeSelect"
          placeholder="Select one"
          :reduce="(option) => option.value"
          v-model="state.form.trip_type"
          :invalid-feedback="errors.trip_type || state.serverErrors.trip_type"
        ></CustomSelect>
      </div>
      <div class="mt-3">
        <Label
          required
          :html-for="fieldIds.commentType"
          :label-id="fieldIds.commentTypeLabel"
        >
          Comment type
        </Label>
        <CustomSelect
          :input-id="fieldIds.commentType"
          accessible-label="Comment type"
          :required="true"
          hide-floating-label
          :disabled="isIconMode"
          :close-on-select="true"
          :options="commentTypeSelect"
          placeholder="Select one"
          :searchable="false"
          :reduce="(option) => option.value"
          v-model="state.form.comment_type"
          :invalid-feedback="
            errors.comment_type || state.serverErrors.comment_type
          "
        ></CustomSelect>
      </div>
      <div class="mt-3 d-flex flex-row justify-content-between">
        <Label :label-id="fieldIds.activeLabel">Active</Label>
        <CustomSwitch
          :id="fieldIds.active"
          :disabled="isIconMode"
          type="radio"
          :aria-labelledby="fieldIds.activeLabel"
          :model-value="state.form.status == 1 ? true : false"
          @update:model-value="changeActive"
        ></CustomSwitch>
      </div>
      <div class="mt-3 d-flex flex-row align-items-center">
        <CustomCheck
          data-test-id="room-options-extended-pass-checkbox"
          :id="fieldIds.extendedPass"
          :aria-labelledby="fieldIds.extendedPassLabel"
          :disabled="isIconMode"
          input-aria-label="Extended pass"
          v-model="state.form.is_extended"
          :checked="!!state.form.is_extended"
          class="me-2"
        ></CustomCheck>
        <Label
          class="m-0"
          :html-for="fieldIds.extendedPass"
          :label-id="fieldIds.extendedPassLabel"
        >
          Extended pass
        </Label>
      </div>
      <div v-if="state.form.is_extended" class="mt-3">
        <Label
          :html-for="fieldIds.extendedPassTime"
          :label-id="fieldIds.extendedPassTimeLabel"
        >
          Extended pass time
        </Label>
        <CustomTimePicker
          data-test-id="room-extended-time-picker"
          :input-id="fieldIds.extendedPassTime"
          accessible-label="Extended pass time"
          :readonly="isIconMode"
          format="HH:mm:ss"
          picker-time-format="HH:mm:ss"
          :show-seconds="true"
          v-model="state.form.extended_pass_time"
          :invalid-feedback="
            errors.extended_pass_time || state.serverErrors.extended_pass_time
          "
        ></CustomTimePicker>
      </div>
      <div
        v-if="state.serverRes"
        ref="successMessageRegionRef"
        class="mt-4"
        tabindex="-1"
      >
        <InfoBox
          class="mt-0"
          :class="{ danger: Object.keys(state.serverErrors).length }"
          :title="state.serverRes.title"
          :announce-assertive="
            Boolean(Object.keys(state.serverErrors).length) ||
            state.serverRes?.title === 'Success'
          "
          >{{ state.serverRes.message }}</InfoBox
        >
      </div>
      <div class="text-center">
        <LoadingButton class="me-2 px-4" @click="onCancel" rounded
          >Cancel</LoadingButton
        >
        <LoadingButton
          data-test-id="save-room-button"
          :is-loading="state.isProcessing"
          @click="submit(isUpdateMode)"
          class="mt-4 px-4"
          solid
          rounded
          >{{ isUpdateMode ? "Update" : "Submit" }}</LoadingButton
        >
      </div>
    </div>
  </div>
</template>

<script>
import Label from "@/v3components/shared/Form/Label.vue"
import InputField from "@/v3components/shared/Form/InputField.vue"
import IconCarousel from "@/v3components/shared/Form/IconCarousel.vue"
import CustomSelect from "@/v3components/shared/Form/CustomSelect.vue"
import CustomSwitch from "@/v3components/shared/Form/CustomSwitch.vue"
import CustomCheck from "@/v3components/shared/Form/CustomCheck.vue"
import CustomTimePicker from "@/v3components/shared/Form/CustomTimePicker.vue"
import InfoBox from "@/v3components/shared/Alerts/InfoBox.vue"
import LoadingButton from "@/v3components/shared/Buttons/LoadingButton.vue"
import useVuelidate from "@vuelidate/core"
import { helpers, required, requiredIf } from "@vuelidate/validators"
import { useStore } from "vuex"
import {
  reactive,
  inject,
  computed,
  onMounted,
  onUnmounted,
  ref,
  nextTick
} from "vue"

/** Let sighted users and SR finish success copy before the modal closes. */
const SUCCESS_MODAL_CLOSE_DELAY_MS = 2400

export default {
  components: {
    Label,
    InputField,
    IconCarousel,
    CustomSelect,
    CustomSwitch,
    CustomCheck,
    CustomTimePicker,
    InfoBox,
    LoadingButton
  },
  props: {
    editLocation: {
      type: Object,
      default: null
    },
    editIconMode: {
      type: Boolean,
      default: false
    }
  },
  emits: ["cancel"],
  setup(props, { emit }) {
    const successMessageRegionRef = ref(null)
    let successCloseTimerId = null

    const clearSuccessCloseTimer = () => {
      if (successCloseTimerId != null) {
        clearTimeout(successCloseTimerId)
        successCloseTimerId = null
      }
    }

    const focusSuccessMessageRegion = async () => {
      await nextTick()
      requestAnimationFrame(() => {
        const el = successMessageRegionRef.value
        if (!el || typeof el.focus !== "function") return
        try {
          el.focus({ preventScroll: true })
        } catch {
          el.focus()
        }
      })
    }

    const scheduleModalCloseAfterSuccess = async () => {
      clearSuccessCloseTimer()
      await focusSuccessMessageRegion()
      successCloseTimerId = window.setTimeout(() => {
        successCloseTimerId = null
        emit("cancel")
      }, SUCCESS_MODAL_CLOSE_DELAY_MS)
    }

    const fieldIds = {
      name: "room-name",
      nameLabel: "room-name-label",
      iconLabel: "room-icon-label",
      tripType: "room-trip-type",
      tripTypeLabel: "room-trip-type-label",
      commentType: "room-comment-type",
      commentTypeLabel: "room-comment-type-label",
      active: "room-active",
      activeLabel: "room-active-label",
      extendedPass: "room-extended-pass",
      extendedPassLabel: "room-extended-pass-label",
      extendedPassTime: "room-extended-pass-time",
      extendedPassTimeLabel: "room-extended-pass-time-label"
    }

    const store = useStore()
    const modal = inject("modal")
    const state = reactive({
      isProcessing: false,
      form: {
        name: "",
        icon: "",
        trip_type: "",
        comment_type: "",
        status: "1",
        is_extended: false,
        extended_pass_time: "01:30:00"
      },
      defaultIconSelect: {
        label: "Select from library",
        value: "Select from library"
      },
      defaultActive: true,
      serverErrors: {},
      serverRes: null
    })

    const tripTypeSelect = [
      { label: "Layover", value: "Layover" },
      { label: "One Way", value: "One Way" },
      { label: "Roundtrip", value: "Roundtrip" }
    ]

    const commentTypeSelect = [
      { label: "Mandatory", value: "Mandatory" },
      { label: "Optional", value: "Optional" },
      { label: "Hidden", value: "Hidden" }
    ]

    const isUpdateMode = computed(() => Boolean(props.editLocation))

    const isIconMode = computed(() => Boolean(props.editIconMode))

    onMounted(() => {
      if (props.editLocation) {
        setFormData({
          name: props.editLocation.name,
          icon: props.editLocation.icon,
          trip_type: props.editLocation.trip_type,
          comment_type: props.editLocation.comment_type,
          status: props.editLocation.status,
          is_extended: !!props.editLocation.nurses,
          extended_pass_time: props.editLocation.nurses
            ? props.editLocation.nurses.end_time
            : "01:30:00"
        })
      }
    })

    const setFormData = (data) => {
      state.form = Object.assign(state.form, data)
    }

    const changeActive = (isActive) => {
      state.defaultActive = isActive
      state.form.status = isActive ? "1" : "0"
    }

    const validationMessages = {
      required: "This field is required"
    }
    const validations = {
      form: {
        name: {
          required: helpers.withMessage(validationMessages.required, required)
        },
        trip_type: {
          required: helpers.withMessage(validationMessages.required, required)
        },
        comment_type: {
          required: helpers.withMessage(validationMessages.required, required)
        },
        status: {
          required: helpers.withMessage(validationMessages.required, required)
        },
        extended_pass_time: {
          required: requiredIf(() => {
            return state.form.is_extended
          })
        }
      }
    }
    const v$ = useVuelidate(validations.form, state.form)

    const isFormValid = computed(() => !v$.value.$invalid)

    const errors = computed(() => {
      const errorObj = {}
      v$.value.$errors.forEach((err) => {
        errorObj[err.$property] = err.$message
      })

      return errorObj
    })

    const submit = () => {
      if (isFormValid.value) {
        if (!state.form.is_extended) {
          state.form.extended_pass_time = null
        }
        if (isUpdateMode.value) {
          const data = {
            roomId: props.editLocation.id,
            data: state.form
          }
          updateRoom(data)
        } else {
          createRoom()
        }
      } else {
        v$.value.$touch()
      }
    }

    const createRoom = () => {
      state.isProcessing = true
      store
        .dispatch("rooms/createRoom", state.form)
        .then(() => {
          resetFormData()
          setResponseInfoBox("Success", "Successfully created!")
          state.isProcessing = false
          state.serverErrors = {}
          modal.setAction("createRoom")
          scheduleModalCloseAfterSuccess()
        })
        .catch((err) => {
          const res = err.response.data
          state.serverErrors = res.errors ? res.errors : {}
          setResponseInfoBox("Error", res.message)
          state.isProcessing = false
        })
    }

    const updateRoom = (data) => {
      state.isProcessing = true
      store
        .dispatch("rooms/updateRoom", data)
        .then((res) => {
          const data = res.data
          resetFormData()
          setResponseInfoBox("Success", "Successfully updated!")
          state.isProcessing = false
          state.serverErrors = {}
          store.commit("rooms/UPDATE_ROOM", data?.data)
          modal.setAction("updateRoom")
          scheduleModalCloseAfterSuccess()
        })
        .catch((err) => {
          const res = err.response.data
          state.serverErrors = res.errors ? res.errors : {}
          setResponseInfoBox("Error", res.message)
          state.isProcessing = false
        })
    }

    const setResponseInfoBox = (title, message) => {
      if (title || message) {
        state.serverRes = {
          message,
          title
        }
      } else {
        state.serverRes = null
      }
    }

    const onCancel = () => {
      clearSuccessCloseTimer()
      resetFormData()
      emit("cancel")
    }

    onUnmounted(() => {
      clearSuccessCloseTimer()
    })

    const resetFormData = () => {
      state.form = {
        name: "",
        icon: "",
        trip_type: "",
        comment_type: "",
        status: "1",
        is_extended: false,
        extended_pass_time: "01:30:00"
      }
    }

    return {
      fieldIds,
      successMessageRegionRef,
      state,
      tripTypeSelect,
      commentTypeSelect,
      isUpdateMode,
      isIconMode,
      changeActive,
      v$,
      isFormValid,
      errors,
      submit,
      setResponseInfoBox,
      onCancel,
      resetFormData
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/assets/scss/_variables.scss";

:deep(.v3-icon-carousel-slide-icon-button:focus-visible) {
  outline: 2px solid $outline-color;
  outline-offset: $outline-offset;
  border-radius: 4px;
}
</style>
